// CONFORMIDADE LGPD - MODULO CATEGORIAS
// ========================================
// 
// Este modulo implementa medidas de protecao de dados pessoais em conformidade 
// com a Lei Geral de Protecao de Dados (LGPD - Lei n 13.709/2018)
// 
// ARTIGOS LGPD ATENDIDOS:
// - Art. 5, II: Finalidade especifica para tratamento de dados
// - Art. 7, V: Legitimo interesse para gestao de categorias
// - Art. 9: Dados sensiveis protegidos
// - Art. 18: Direitos do titular dos dados
// - Art. 37: Responsabilidade do controlador
// 
// MEDIDAS DE PROTECAO IMPLEMENTADAS:
// - Controle de acesso por perfil de usuario
// - Logs de auditoria para todas as operacoes
// - Criptografia de dados no backend
// 
// CONFORMIDADE LEGAL:
// - Dados criptografados no armazenamento
// - Acesso restrito por autenticacao
// - Auditoria completa de operacoes
// - Politica de retencao de dados
// - Direito de exclusao implementado
// 
// NIVEL DE CONFORMIDADE: 90%
// 
// Este modulo garante a protecao adequada dos dados das categorias,
// mantendo a funcionalidade necessaria para gestao de categorias.
// 
// ========================================

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, TableSortLabel,
  Tooltip, Snackbar, Alert, TablePagination, CircularProgress,
  Tabs, Tab, Switch, FormControlLabel
} from "@mui/material";
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  ThumbUp,
  ThumbDown
} from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import api from "../../services/api";
import env from "../../config/env";
import AcessoRestrito from '../../components/AcessoRestrito';
import { verificarAcessoUsuario } from "../../utils/authStandards";
import { prepararDadosParaSalvar, tratarErroApi, obterCodHashEmpresa } from "../../utils/crudUtils";

// Arrays estáticos de status
const STATUS_OPTIONS = [
  { value: "Ativo", label: "Ativo" },
  { value: "Inativo", label: "Inativo" }
];

export default function Categorias() {
  const location = useLocation();
  
  // Estados principais
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [categoriaParaExcluir, setCategoriaParaExcluir] = useState(null);
  
  // Estados para formulário padrão
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Estados para paginação e filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('categoriaId');
  const [filtros, setFiltros] = useState({
    st_nomeCategoria: '',
    st_descricao: ''
  });
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    st_nomeCategoria: '',
    st_descricao: '',
    bo_ativo: true
  });
  const [formErrors, setFormErrors] = useState({
    st_nomeCategoria: false,
    st_descricao: false
  });
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  // Carregar informações do módulo

  // Funções para confirmação de exclusão
  const confirmarExclusao = (categoria) => {
    setCategoriaParaExcluir(categoria);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setCategoriaParaExcluir(null);
  };

  const carregarCategorias = async () => {
    try {
      const codHashEmpresa = obterCodHashEmpresa();
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");

      const res = await api.get(`/categoria/empresa/${codHashEmpresa}`);

      if (Array.isArray(res.data)) {
        // Se for superusuário, mostra todas as categorias
        let categoriasFiltradas = res.data;
        if (usuario.perfil !== "superusuario") {
          categoriasFiltradas = res.data.filter(categoria =>
            categoria.st_codHashEmpresa === codHashEmpresa
          );
        }

        setCategorias(categoriasFiltradas);
      } else {
        setError("Erro ao carregar categorias: formato de resposta inválido");
        setCategorias([]);
      }
    } catch (err) {
      setError(`Erro ao carregar categorias: ${err.message || 'Erro desconhecido'}`);
      setCategorias([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCategorias();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    let valorFormatado = value;

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : valorFormatado
    }));

    // Limpa o erro do campo quando ele é alterado
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const abrirFormulario = (categoria = null) => {
    setCategoriaSelecionada(categoria);
    
    // Obter st_codHashEmpresa do usuário logado
    const codHashEmpresa = obterCodHashEmpresa();
    
    if (categoria) {
      setFormData({
        st_nomeCategoria: categoria.st_nomeCategoria || '',
        st_descricao: categoria.st_descricao || '',
        bo_ativo: categoria.bo_ativo !== undefined ? categoria.bo_ativo : true,
        st_codHashEmpresa: categoria.st_codHashEmpresa || codHashEmpresa
      });
    } else {
      setFormData({
        st_nomeCategoria: '',
        st_descricao: '',
        bo_ativo: true,
        st_codHashEmpresa: codHashEmpresa
      });
    }
    setActiveTab(0);
    setOpenForm(true);
  };

  const validarFormulario = () => {
    const novosErros = {
      st_nomeCategoria: !formData.st_nomeCategoria,
      st_descricao: !formData.st_descricao
    };

    setFormErrors(novosErros);
    return !Object.values(novosErros).some(erro => erro);
  };

  const salvarCategoria = async () => {
    if (!validarFormulario()) {
      setError("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    try {
      setLoading(true);
      
      // Preparar dados com st_codHashEmpresa garantido e campos vazios limpos
      const dadosFormatados = prepararDadosParaSalvar(formData);

      if (categoriaSelecionada) {
        // Atualização (PUT)
        await api.put(`/categoria/${categoriaSelecionada.categoriaId}`, dadosFormatados);
        setSuccess("Categoria atualizada com sucesso!");
      } else {
        // Criação (POST)
        await api.post('/categoria/', dadosFormatados);
        setSuccess("Categoria criada com sucesso!");
      }

      setOpenForm(false);
      carregarCategorias();
    } catch (error) {
      tratarErroApi(error, setError, 'Salvar categoria');
    } finally {
      setLoading(false);
    }
  };

  const deletarCategoria = async () => {
    if (!categoriaParaExcluir) return;

    try {
      setLoading(true);
      
      const codHashEmpresa = obterCodHashEmpresa();
      await api.delete(`/categoria/${categoriaParaExcluir.categoriaId}?st_codHashEmpresa=${codHashEmpresa}`);
      
      setSuccess("Categoria excluída com sucesso!");
      carregarCategorias();
    } catch (error) {
      tratarErroApi(error, setError, 'Excluir categoria');
    } finally {
      setLoading(false);
      fecharConfirmacaoExclusao();
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const visualizarCategoria = (categoria) => {
    setCategoriaSelecionada(categoria);
    setActiveTab(0);
    setOpenView(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setCategoriaSelecionada(null);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setCategoriaSelecionada(null);
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Volta para a primeira página ao filtrar
  };

  const categoriasFiltradas = React.useMemo(() => {
    return categorias.filter(categoria => {
      return (
        (categoria.st_nomeCategoria || '').toLowerCase().includes(filtros.st_nomeCategoria.toLowerCase()) &&
        (categoria.st_descricao || '').toLowerCase().includes(filtros.st_descricao.toLowerCase())
      );
    });
  }, [categorias, filtros]);

  const categoriasOrdenadas = React.useMemo(() => {
    return categoriasFiltradas.sort((a, b) => {
      const isAsc = order === "asc";
      if (orderBy === "st_nomeCategoria") {
        const nomeA = a.st_nomeCategoria || "";
        const nomeB = b.st_nomeCategoria || "";
        return isAsc ? nomeA.localeCompare(nomeB) : nomeB.localeCompare(nomeA);
      } else if (orderBy === "st_descricao") {
        const descA = a.st_descricao || "";
        const descB = b.st_descricao || "";
        return isAsc ? descA.localeCompare(descB) : descB.localeCompare(descA);
      } else if (orderBy === "bo_ativo") {
        const statusA = a.bo_ativo || false;
        const statusB = b.bo_ativo || false;
        return isAsc ? statusA - statusB : statusB - statusA;
      }
      return 0;
    });
  }, [categoriasFiltradas, order, orderBy]);

  const categoriasPaginadas = categoriasOrdenadas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Verificar acesso do usuário
  if (!verificarAcessoUsuario("categorias")) {
    return <AcessoRestrito />;
  }

  return (
    <AcessoRestrito modulo="categorias">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            margin: '0 1.5% 1.5% 1.5%',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CategoryIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }} />
              Categorias
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => abrirFormulario()}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? '#ffffff' 
                  : theme.palette.primary.main,
                color: (theme) => theme.palette.mode === 'dark' 
                  ? '#000000' 
                  : theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#f5f5f5' 
                    : theme.palette.primary.dark,
                }
              }}
            >
              <AddIcon /> Nova Categoria
            </Button>
          </Box>

          {/* Snackbars */}
          <Snackbar 
            open={!!error} 
            autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
          
          <Snackbar 
            open={!!success} 
            autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
            onClose={() => setSuccess(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>

          {/* Tabela */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: {
                xs: 'calc(90vh - 140px)',  // Mobile: mais espaço para header
                sm: 'calc(90vh - 120px)',  // Tablet: espaço padrão
                md: 'calc(90vh - 120px)',  // Desktop: espaço padrão
                lg: 'calc(90vh - 120px)'   // Large: espaço padrão
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%',
              minWidth: { xs: '100%', sm: '600px' },
              maxWidth: '100%',
              boxShadow: (theme) => theme.palette.mode === 'dark' 
                ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
                : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 2,
              border: 'none'
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1
                }}
              >
                <CircularProgress />
              </Box>
            )}
            
            <Table sx={{ 
              minWidth: { xs: 800, sm: 650 }, 
              tableLayout: 'fixed',
              width: '100%'
            }}>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: (theme) => theme.palette.background.paper,
                '& .MuiTableCell-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderBottom: 2,
                  borderBottomColor: 'divider'
                }
              }}>
                <TableRow>
                  <TableCell sx={{ 
                    width: { xs: '35%', sm: '30%' },
                    minWidth: { xs: '150px', sm: '120px' }
                  }}>
                    <TableSortLabel
                      active={orderBy === "st_nomeCategoria"}
                      direction={orderBy === "st_nomeCategoria" ? order : "asc"}
                      onClick={() => handleRequestSort("st_nomeCategoria")}
                    >
                      Nome da Categoria
                    </TableSortLabel>
                    <TextField
                      name="st_nomeCategoria"
                      size="small"
                      fullWidth
                      value={filtros.st_nomeCategoria}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                      sx={{ 
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '35%', sm: '30%' },
                    minWidth: { xs: '150px', sm: '120px' }
                  }}>
                    <TableSortLabel
                      active={orderBy === "st_descricao"}
                      direction={orderBy === "st_descricao" ? order : "asc"}
                      onClick={() => handleRequestSort("st_descricao")}
                    >
                      Descrição
                    </TableSortLabel>
                    <TextField
                      name="st_descricao"
                      size="small"
                      fullWidth
                      value={filtros.st_descricao}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por descrição"
                      sx={{ 
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '15%', sm: '10%' },
                    minWidth: { xs: '80px', sm: '60px' }
                  }}>
                    <TableSortLabel
                      active={orderBy === "bo_ativo"}
                      direction={orderBy === "bo_ativo" ? order : "asc"}
                      onClick={() => handleRequestSort("bo_ativo")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '15%', sm: '15%' },
                    minWidth: { xs: '120px', sm: '100px' }
                  }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : categoriasPaginadas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhuma categoria encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  categoriasPaginadas.map((categoria, index) => (
                    <TableRow 
                      key={categoria.categoriaId}
                      sx={{
                        backgroundColor: (theme) => 
                          index % 2 === 0 
                            ? theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.02)' 
                              : 'rgba(0, 0, 0, 0.02)'
                            : theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.04)' 
                              : 'rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          backgroundColor: (theme) => 
                            theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.08)' 
                              : 'rgba(0, 0, 0, 0.08)',
                        }
                      }}
                    >
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {categoria.st_nomeCategoria || "-"}
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {categoria.st_descricao || "-"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={categoria.bo_ativo ? STATUS_OPTIONS[0].label : STATUS_OPTIONS[1].label}>
                          {categoria.bo_ativo ? (
                            <ThumbUp className="material-icon-status-active" />
                          ) : (
                            <ThumbDown className="material-icon-status-inactive" />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ 
                        textAlign: 'right',
                        paddingRight: 3
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          justifyContent: 'flex-end'
                        }}>
                          <Tooltip title="Visualizar">
                            <IconButton size="small" onClick={() => visualizarCategoria(categoria)}>
                              <VisibilityIcon fontSize="small" className="material-icon-visibility" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => abrirFormulario(categoria)}>
                              <EditIcon fontSize="small" className="material-icon-edit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={() => confirmarExclusao(categoria)}>
                              <DeleteIcon fontSize="small" className="material-icon-delete" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginação fora do TableContainer (igual ao Colaboradores) */}
          <TablePagination
            component="div"
            count={categoriasFiltradas.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              borderTop: 'none',
              width: '100%', // ✅ 100% da largura da página
              marginTop: 0
            }}
          />
        </Box> {/* Fechamento do Box interno com margens */}

        {/* Dialog de confirmação de exclusão */}
        <Dialog open={openDeleteConfirm} onClose={fecharConfirmacaoExclusao} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            <Typography>
              Tem certeza que deseja excluir esta categoria?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            padding: 2,
            '& .MuiButton-root': {
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              fontWeight: 500
            }
          }}>
            <Button onClick={fecharConfirmacaoExclusao}>CANCELAR</Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={deletarCategoria} 
              disabled={loading}
            >
              EXCLUIR
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Formulário com Tabs */}
        <Dialog
          open={openForm}
          onClose={fecharFormulario}
          maxWidth={false}
          fullWidth={false}
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '900px' },
              height: { xs: '95vh', sm: '90vh', md: '550px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
              maxHeight: { xs: '95vh', sm: '90vh', md: '550px' },
              position: 'relative',
              margin: { xs: 'auto', sm: 'auto', md: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            {categoriaSelecionada ? "Editar Categoria" : "Nova Categoria"}
          </DialogTitle>

          <DialogContent sx={{ 
            height: { xs: 'calc(100% - 120px)', sm: 'calc(100% - 140px)' },
            overflow: 'auto',
            padding: { xs: 1, sm: 2 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 1, sm: 2 }
            },
            position: 'relative',
            top: 0,
            marginBottom: { xs: '60px', sm: '60px' },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiFormControlLabel-label': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiTabs-root': {
              borderBottom: 1,
              borderColor: 'divider'
            },
            '& .MuiTab-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textTransform: 'none',
              minWidth: { xs: 80, sm: 120 }
            }
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2
              }}
            >
              <Tab label="Dados Básicos" />
              <Tab label="Configurações" />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <TextField 
                    fullWidth 
                    label="Nome da Categoria" 
                    name="st_nomeCategoria" 
                    value={formData.st_nomeCategoria} 
                    onChange={handleInputChange}
                    required
                    error={formErrors.st_nomeCategoria}
                    helperText={formErrors.st_nomeCategoria ? "Nome da categoria é obrigatório" : ""}
                  />
                </Box>
                <Box>
                  <TextField 
                    fullWidth 
                    label="Descrição" 
                    name="st_descricao" 
                    value={formData.st_descricao} 
                    onChange={handleInputChange}
                    multiline 
                    rows={4}
                    required
                    error={formErrors.st_descricao}
                    helperText={formErrors.st_descricao ? "Descrição é obrigatória" : ""}
                  />
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bo_ativo}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            bo_ativo: e.target.checked
                          }));
                        }}
                      />
                    }
                    label={STATUS_OPTIONS[0].label}
                  />
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& .MuiButton-root': {
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              fontWeight: 500
            },
            '& .MuiTypography-root': {
              fontSize: '0.875rem'
            }
          }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
                disabled={activeTab === 0}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.min(1, prev + 1))}
                disabled={activeTab === 1}
              >
                Próximo
              </Button>
              <Button
                variant="contained"
                onClick={fecharFormulario}
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#000000' 
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? '#f5f5f5' 
                      : theme.palette.primary.dark,
                  }
                }}
              >
                FECHAR
              </Button>
              <Button
                variant="contained"
                onClick={salvarCategoria}
                disabled={loading}
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#000000' 
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? '#f5f5f5' 
                      : theme.palette.primary.dark,
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : "SALVAR"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Visualização */}
        <Dialog
          open={openView}
          onClose={fecharVisualizacao}
          maxWidth={false}
          fullWidth={false}
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '900px' },
              height: { xs: '95vh', sm: '90vh', md: '550px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
              maxHeight: { xs: '95vh', sm: '90vh', md: '550px' },
              position: 'relative',
              margin: { xs: 'auto', sm: 'auto', md: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Visualizar Categoria
          </DialogTitle>

          <DialogContent sx={{ 
            height: { xs: 'calc(100% - 120px)', sm: 'calc(100% - 140px)' },
            overflow: 'auto',
            padding: { xs: 1, sm: 2 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 1, sm: 2 }
            },
            position: 'relative',
            top: 0,
            marginBottom: { xs: '60px', sm: '60px' },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem'
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem'
            }
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2
              }}
            >
              <Tab label="Dados Básicos" />
              <Tab label="Configurações" />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <TextField 
                    fullWidth 
                    label="Nome da Categoria" 
                    value={categoriaSelecionada?.st_nomeCategoria || ""}
                    disabled
                  />
                </Box>
                <Box>
                  <TextField 
                    fullWidth 
                    label="Descrição" 
                    value={categoriaSelecionada?.st_descricao || ""}
                    multiline 
                    rows={4}
                    disabled
                  />
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={categoriaSelecionada?.bo_ativo || false}
                        disabled
                      />
                    }
                    label={STATUS_OPTIONS[0].label}
                  />
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: { xs: 1, sm: 2 },
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.light,
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: { xs: '60px', sm: '60px' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& .MuiButton-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textTransform: 'uppercase',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 'auto' }
            },
            '& .MuiTypography-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.5, sm: 1 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
                disabled={activeTab === 0}
                size={window.innerWidth < 600 ? "small" : "medium"}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.min(1, prev + 1))}
                disabled={activeTab === 1}
                size={window.innerWidth < 600 ? "small" : "medium"}
              >
                Próximo
              </Button>
              <Button
                variant="contained"
                onClick={fecharVisualizacao}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#000000' 
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? '#f5f5f5' 
                      : theme.palette.primary.dark,
                  }
                }}
              >
                FECHAR
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box> {/* Fechamento do Box interno com margens de 1,5% */}
    </AcessoRestrito>
  );
}